import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

interface Signal {
    _id: string;  // MongoDB _id
    message: string;
    createdAt: string;
    isRead: boolean;
    metadata: {
        type: string;
        coin: string;
        timeframe: string;
        indicator: string;
        condition: string;
        target: number;
        note: string;
        status: string;
    };
}

const IncomingSignals: React.FC = () => {
    const [signals, setSignals] = useState<Signal[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSignals();
        // Her 30 saniyede bir yeni sinyalleri kontrol et
        const interval = setInterval(fetchSignals, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchSignals = async () => {
        try {
            const response = await axios.get('/api/signals');
            setSignals(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Sinyaller alınırken hata oluştu:', error);
            setLoading(false);
        }
    };

    const markAsRead = async (id: string) => {
        try {
            await axios.patch(`/api/signals/${id}/read`);
            setSignals(signals.map(signal => 
                signal._id === id ? { ...signal, isRead: true } : signal
            ));
        } catch (error) {
            console.error('Sinyal okundu işaretlenirken hata:', error);
        }
    };

    const deleteSignal = async (id: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent triggering the markAsRead event
        try {
            await axios.delete(`/api/signals/${id}`);
            setSignals(signals.filter(signal => signal._id !== id));
        } catch (error) {
            console.error('Sinyal silinirken hata:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await axios.put('/api/notifications/mark-all-read');
            setSignals(signals.map(signal => ({ ...signal, isRead: true })));
        } catch (error) {
            console.error('Tüm sinyaller okundu işaretlenirken hata:', error);
        }
    };

    if (loading) {
        return <LoadingText>Yükleniyor...</LoadingText>;
    }

    return (
        <Container>
            <Title>Gelen Sinyaller</Title>
            {signals.some(signal => !signal.isRead) && (
                <MarkAllButton onClick={markAllAsRead}>
                    Tümünü Okundu İşaretle
                </MarkAllButton>
            )}
            <SignalsList>
                {signals.length > 0 ? (
                    signals.map((signal) => (
                        <SignalItem 
                            key={signal._id} 
                            $isRead={signal.isRead}
                            onClick={() => !signal.isRead && markAsRead(signal._id)}
                        >
                            <SignalIcon>{signal.isRead ? '📫' : '📬'}</SignalIcon>
                            <SignalContent>
                                <SignalMessage>
                                    {signal.message.split('\n').map((line, i) => (
                                        <MessageLine key={i}>{line}</MessageLine>
                                    ))}
                                </SignalMessage>
                                <SignalTime>
                                    {format(new Date(signal.createdAt), 'dd MMMM yyyy HH:mm', { locale: tr })}
                                </SignalTime>
                            </SignalContent>
                            <DeleteButton onClick={(e) => deleteSignal(signal._id, e)}>
                                🗑️
                            </DeleteButton>
                            {!signal.isRead && <UnreadDot />}
                        </SignalItem>
                    ))
                ) : (
                    <EmptyState>
                        <EmptyIcon>📭</EmptyIcon>
                        <EmptyText>Henüz hiç sinyal bulunmuyor</EmptyText>
                    </EmptyState>
                )}
            </SignalsList>
        </Container>
    );
};

const Container = styled.div`
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
`;

const Title = styled.h1`
    color: #E2E8F0;
    margin-bottom: 24px;
`;

const SignalsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const SignalItem = styled.div<{ $isRead: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px;
    background: ${props => props.$isRead ? 'rgba(15, 23, 42, 0.3)' : 'rgba(59, 130, 246, 0.1)'};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        transform: translateX(5px);
        background: ${props => props.$isRead ? 'rgba(15, 23, 42, 0.4)' : 'rgba(59, 130, 246, 0.2)'};
    }
`;

const SignalIcon = styled.div`
    font-size: 1.5rem;
`;

const SignalContent = styled.div`
    flex: 1;
`;

const SignalMessage = styled.div`
    color: #E2E8F0;
    margin-bottom: 8px;
    white-space: pre-wrap;
`;

const MessageLine = styled.div`
    margin-bottom: 4px;
    line-height: 1.5;
`;

const SignalTime = styled.div`
    color: #64748B;
    font-size: 0.9rem;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.2rem;
    padding: 4px 8px;
    color: #64748B;
    opacity: 0.7;
    transition: all 0.2s ease;

    &:hover {
        opacity: 1;
        color: #EF4444;
    }
`;

const UnreadDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #3B82F6;
`;

const LoadingText = styled.div`
    color: #E2E8F0;
    text-align: center;
    padding: 24px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 48px;
    background: rgba(15, 23, 42, 0.3);
    border-radius: 12px;
`;

const EmptyIcon = styled.div`
    font-size: 3rem;
`;

const EmptyText = styled.div`
    color: #64748B;
    text-align: center;
`;

const MarkAllButton = styled.button`
    background-color: #4a90e2;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: 16px;
    font-size: 14px;

    &:hover {
        background-color: #357abd;
    }
`;

export default IncomingSignals;
